import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, ClipboardList, Download, RefreshCw, PlayCircle, Save } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: { label: 'En attente', cls: 'bg-amber-500 text-white hover:bg-amber-500' },
  zero_confirmed: { label: '0 confirmé', cls: 'bg-emerald-600 text-white hover:bg-emerald-600' },
  answered: { label: 'Renseigné', cls: 'bg-emerald-600 text-white hover:bg-emerald-600' },
  denied_zero: { label: '"Non" zéro', cls: 'bg-rose-500 text-white hover:bg-rose-500' },
  relance_sent: { label: 'Relance', cls: 'bg-orange-500 text-white hover:bg-orange-500' },
};

const DailyReports = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [qcmText, setQcmText] = useState('');
  const [savingText, setSavingText] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings/qcm-text`)
      .then(r => setQcmText(r.data?.text || ''))
      .catch(() => {});
  }, []);

  const handleSaveQcmText = async () => {
    setSavingText(true);
    try {
      await axios.put(`${API}/admin/settings/qcm-text`, { text: qcmText });
      toast.success('Texte du QCM enregistré');
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingText(false);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const res = await axios.get(`${API}/admin/daily-reports`, { params });
      setItems(res.data || []);
    } catch {
      toast.error('Erreur de chargement des reportings');
    } finally {
      setLoading(false);
    }
  }, [from, to, statusFilter, q]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const url = `${API}/admin/daily-reports/export-csv?${params.toString()}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `daily_reports_${from || 'all'}_${to || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Erreur lors de l'export CSV");
    }
  };

  const handleRunNow = async (job) => {
    try {
      const res = await axios.get(`${API}/admin/daily-reports/run-now?job=${job}`);
      toast.success(`Job "${job}" exécuté (${res.data.count})`);
      fetchItems();
    } catch {
      toast.error("Erreur lors du lancement");
    }
  };

  return (
    <AdminLayout title="Reportings journaliers" subtitle="QCM envoyés aux dentistes (lun-ven 18h30)">
      <Card className="mb-4" data-testid="qcm-text-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Texte du QCM (modifiable)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Ce texte est inséré au début du message QCM envoyé chaque soir aux
            dentistes. Vous pouvez le modifier librement à tout moment.
          </p>
          <Textarea
            rows={3}
            value={qcmText}
            onChange={(e) => setQcmText(e.target.value)}
            placeholder="Cher client, afin d'éviter tout risque de perte d'empreinte ou d'affectation, merci de renseigner ce petit reporting journalier."
            data-testid="qcm-text-input"
          />
          <Button onClick={handleSaveQcmText} disabled={savingText} data-testid="qcm-text-save">
            {savingText ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer le texte
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="reports-page">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            QCM ({items.length})
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => handleRunNow('create')} data-testid="run-create">
              <PlayCircle className="h-4 w-4 mr-2" />Lancer 18h30
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleRunNow('zero_check')} data-testid="run-zero">
              <PlayCircle className="h-4 w-4 mr-2" />Lancer 22h30
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleRunNow('relance')} data-testid="run-relance">
              <PlayCircle className="h-4 w-4 mr-2" />Lancer relance 9h
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleRunNow('inactive_dentists')} data-testid="run-inactive">
              <PlayCircle className="h-4 w-4 mr-2" />Lancer alerte 5j
            </Button>
            <Button variant="outline" size="sm" onClick={fetchItems} data-testid="reports-refresh">
              <RefreshCw className="h-4 w-4 mr-2" />Actualiser
            </Button>
            <Button onClick={handleExport} data-testid="reports-export-csv">
              <Download className="h-4 w-4 mr-2" />Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Input placeholder="Recherche client / patient / texte" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reports-search" />
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="reports-status"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="answered">Renseigné</SelectItem>
                <SelectItem value="zero_confirmed">0 confirmé</SelectItem>
                <SelectItem value="denied_zero">"Non" zéro</SelectItem>
                <SelectItem value="relance_sent">Relance</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="reports-from" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reports-to" />
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Aucun reporting</div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Date</TableHead>
                    <TableHead>Cabinet</TableHead>
                    <TableHead>Dentiste</TableHead>
                    <TableHead>Structure</TableHead>
                    <TableHead className="text-center">Empreintes</TableHead>
                    <TableHead>Patients</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Réponse</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => {
                    const status = STATUS_BADGE[r.status] || { label: r.status, cls: 'bg-slate-500 text-white' };
                    const fullName = [r.client_first_name, r.client_last_name].filter(Boolean).join(' ');
                    return (
                      <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                        <TableCell className="font-mono text-xs">{r.report_date}</TableCell>
                        <TableCell className="font-medium">{r.client_name}</TableCell>
                        <TableCell>{fullName || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.client_structure || '—'}</TableCell>
                        <TableCell className="text-center font-semibold">{r.num_empreintes ?? '—'}</TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={(r.patient_names || []).join(', ')}>
                          {(r.patient_names || []).join(', ') || '—'}
                        </TableCell>
                        <TableCell><Badge className={status.cls}>{status.label}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {r.answered_at ? new Date(r.answered_at).toLocaleString('fr-FR') : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default DailyReports;
